import { Component, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';

import { Subscription } from 'rxjs';

import { languageService, languageSupport } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';

import { AskWaiterView } from '@unpispas/upp-data';
import { dataService } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

import { ViewRenderer } from '@unpispas/upp-wdgt';

type WaiterPayAmount = { Int: string; Dec: string; Str: string; Neg: boolean };

/** Guest wait panel (AskWaiter or Stripe onlineWait). */
@Component({
    selector: 'upp-guest-waiter',
    templateUrl: './upp-waiter.html',
    styleUrls: ['./upp-waiter.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppGuestWaiterComponent extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input() qrcode: QrCodeView | null = null;
    @Input() onlineWait = false;
    @Input() onlineTickets: TicketView[] = [];
    @Input() pendingPayTickets: TicketView[] = [];
    @Output() Close = new EventEmitter<boolean>();

    /********************************/
    /* Subscriptions                */
    /********************************/

    private _qrcode_subscription: Subscription | null = null;
    private _minute_subscription: Subscription | null = null;

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        private data: dataService,
        public view: viewService,
    ) {
        super(lang, change.cdref);
    }

    /********************************/
    /* Mode                         */
    /********************************/

    get IsOnlineWait(): boolean {
        return this.onlineWait;
    }

    /** Cash/card (or orphan PP) wait — not a generic “call waiter” hold. */
    get IsPayWait(): boolean {
        return this.IsOnlineWait
            || this.PayMethod !== null
            || this.OrphanPendingPayTickets.length > 0;
    }

    /********************************/
    /* Requests / online tickets    */
    /********************************/

    get Requests(): Array<AskWaiterView> {
        return this.qrcode?.requests ?? [];
    }

    /** Stable keys for {@link pendingPayTickets} scope, or null when unscoped. */
    private get _FocusKeys(): Set<string> | null {
        if (this.pendingPayTickets.length === 0) {
            return null;
        }

        const _keys = new Set<string>();
        for (const ticket of this.pendingPayTickets) {
            // Index both ids: AskWaiter.ticket may resolve with objid while the
            // hold still carries the client _uuid (or the reverse).
            if (ticket?._uuid) {
                _keys.add(String(ticket._uuid));
            }
            if (ticket?.objid != null && ticket.objid !== '') {
                _keys.add(String(ticket.objid));
            }
        }
        return _keys.size > 0 ? _keys : null;
    }

    private _InFocus(ticket: TicketView | null | undefined): boolean {
        if (this.pendingPayTickets.length === 0) {
            return true;
        }
        if (!ticket) {
            return false;
        }
        // Canonical instance identity first (precondition: unique model instances).
        if (this.pendingPayTickets.some((held) => held === ticket)) {
            return true;
        }
        const _focus = this._FocusKeys;
        if (!_focus) {
            return false;
        }
        if (ticket._uuid && _focus.has(String(ticket._uuid))) {
            return true;
        }
        if (ticket.objid != null && ticket.objid !== '' && _focus.has(String(ticket.objid))) {
            return true;
        }
        return false;
    }

    get TicketRequests(): Array<AskWaiterView> {
        return this.Requests.filter(
            (request) => !!request.ticket && this._InFocus(request.ticket),
        );
    }

    /**
     * Pending-pay tickets (`IsToPay`) with no live AskWaiter — cash/card or stale
     * `PAYLINE` (no live Stripe `onlineWait`). Only tickets in the home focus
     * list ({@link pendingPayTickets}); never scan the whole table for orphans
     * (that would merge unrelated payment requests into one screen).
     */
    get OrphanPendingPayTickets(): Array<TicketView> {
        if (this.IsOnlineWait || this.pendingPayTickets.length === 0) {
            return [];
        }

        const _linkedKeys = new Set<string>();
        for (const _request of this.Requests) {
            if (_request.ticket
                && ((_request.reason === 'PAYCASH') || (_request.reason === 'PAYCARD'))) {
                if (_request.ticket._uuid) {
                    _linkedKeys.add(String(_request.ticket._uuid));
                }
                if (_request.ticket.objid != null && _request.ticket.objid !== '') {
                    _linkedKeys.add(String(_request.ticket.objid));
                }
            }
        }

        return this.pendingPayTickets.filter((ticket) => {
            if (!ticket || !ticket.IsToPay) {
                return false;
            }
            if (ticket._uuid && _linkedKeys.has(String(ticket._uuid))) {
                return false;
            }
            if (ticket.objid != null && ticket.objid !== ''
                && _linkedKeys.has(String(ticket.objid))) {
                return false;
            }
            return true;
        });
    }

    get Elapsed(): string | null {
        if (this.IsOnlineWait || this.Requests.length === 0) {
            return null;
        }

        const _oldest = Math.max(...this.Requests.map((request) => {
            const _created = request.created;
            if (!_created) {
                return 0;
            }
            return Math.round((Date.now() - _created.getTime()) / 60000);
        }));

        if (_oldest < 1) {
            return this.tr('@elapsed_time_just_now');
        }
        if (_oldest < 60) {
            return this.tr('@elapsed_time_minutes', [_oldest]);
        }

        const _hh = Math.round(_oldest / 60);
        const _mm = Math.round(_oldest % 60);
        return this.tr('@elapsed_time_hours', [_hh, _mm]);
    }

    get PayMethod(): 'PAYCASH' | 'PAYCARD' | null {
        if (this.IsOnlineWait) {
            return null;
        }

        // Scoped focus / filtered rows: do not use table-wide WaiterReason.
        if (this._FocusKeys) {
            if (this.TicketRequests.some((request) => request.reason === 'PAYCARD')
                || this.OrphanPendingPayTickets.some((ticket) => ticket.payment === 'PAYCARD')) {
                return 'PAYCARD';
            }
            if (this.TicketRequests.some((request) => request.reason === 'PAYCASH')
                || this.OrphanPendingPayTickets.some((ticket) => ticket.payment === 'PAYCASH')) {
                return 'PAYCASH';
            }
            return null;
        }

        const _reason = this.qrcode?.WaiterReason;
        if ((_reason === 'PAYCASH') || (_reason === 'PAYCARD')) {
            return _reason;
        }

        const _orphans = this.OrphanPendingPayTickets;
        if (_orphans.some((ticket) => ticket.payment === 'PAYCARD')) {
            return 'PAYCARD';
        }
        if (_orphans.some((ticket) => ticket.payment === 'PAYCASH')) {
            return 'PAYCASH';
        }

        return null;
    }

    get PayAmount(): WaiterPayAmount | null {
        if (this.IsOnlineWait) {
            const _amount = this.onlineTickets.reduce((total, ticket) => total + (ticket.due || 0), 0);
            return (_amount !== 0) ? UppGuestWaiterComponent._FormatAmount(_amount) : null;
        }

        if (this._FocusKeys) {
            let _scoped = 0;
            for (const request of this.TicketRequests) {
                if (request.reason === 'REFUND') {
                    _scoped -= request.amount || 0;
                }
                else if ((request.reason === 'PAYCASH') || (request.reason === 'PAYCARD')) {
                    _scoped += request.ticket?.price || request.ticket?.due || 0;
                }
            }
            for (const ticket of this.OrphanPendingPayTickets) {
                _scoped += ticket.due || 0;
            }
            _scoped = Math.round(_scoped * 100) / 100;
            return (_scoped !== 0)
                ? UppGuestWaiterComponent._FormatAmount(_scoped)
                : null;
        }

        // Unscoped: never present several independent pay requests as one bill.
        // Only show a combined amount when exactly one pay row is in scope.
        const _payRequests = this.TicketRequests.filter(
            (request) => (request.reason === 'PAYCASH') || (request.reason === 'PAYCARD'),
        );
        const _orphans = this.OrphanPendingPayTickets;
        const _payCount = _payRequests.length + _orphans.length;
        if (_payCount > 1) {
            return null;
        }

        if (_payCount === 1) {
            if (_payRequests.length === 1) {
                const _one = _payRequests[0].ticket?.price
                    || _payRequests[0].ticket?.due
                    || 0;
                return (_one !== 0)
                    ? UppGuestWaiterComponent._FormatAmount(_one)
                    : null;
            }

            const _orphanOne = _orphans[0].due || 0;
            return (_orphanOne !== 0)
                ? UppGuestWaiterComponent._FormatAmount(_orphanOne)
                : null;
        }

        const _amount = this.qrcode?.WaiterAmount ?? 0;
        return (_amount !== 0)
            ? UppGuestWaiterComponent._FormatAmount(_amount)
            : null;
    }

    get CanCancel(): boolean {
        if (this.IsOnlineWait) {
            return false;
        }

        const _requests = this._FocusKeys ? this.TicketRequests : this.Requests;
        if (_requests.length > 0) {
            return !_requests.every((request) => request.reason === 'REFUND');
        }

        // No AskWaiter (in scope): still allow cancel for cash/card or stale PAYLINE PP.
        return this.OrphanPendingPayTickets.length > 0;
    }

    /********************************/
    /* Ticket preview               */
    /********************************/

    private _showticket: TicketView | null = null;
    get ShowTicket(): TicketView | null {
        return this._showticket;
    }

    OnShowTicket(ticket: TicketView | null): void {
        this._showticket = ticket;
        this.change.markForCheck();
    }

    /********************************/
    /* Lifecycle                    */
    /********************************/

    ngOnInit(): void {
        this._minute_subscription = this.data.clock.OnOneMinuteTick.subscribe(
        () => {
            this.change.markForCheck();
        });
        this._BindQrcode();
    }

    ngOnChanges(): void {
        this._BindQrcode();
        this.change.markForCheck();
    }

    ngOnDestroy(): void {
        if (this._qrcode_subscription) {
            this._qrcode_subscription.unsubscribe();
            this._qrcode_subscription = null;
        }

        if (this._minute_subscription) {
            this._minute_subscription.unsubscribe();
            this._minute_subscription = null;
        }

        super.OnDestroy();
    }

    private _BindQrcode(): void {
        if (this._qrcode_subscription) {
            this._qrcode_subscription.unsubscribe();
            this._qrcode_subscription = null;
        }

        if (this.qrcode) {
            this._qrcode_subscription = this.qrcode.OnRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }
    }

    /********************************/
    /* Actions                      */
    /********************************/

    async OnCancel(): Promise<void> {
        if (this.IsOnlineWait) {
            return;
        }

        this.Close.emit(true);
    }

    OnClose(): void {
        this.Close.emit(false);
    }

    RequestTrack(_index: number, request: AskWaiterView): string | null {
        return request?._uuid || null;
    }

    TicketTrack(_index: number, ticket: TicketView): string | null {
        return ticket?._uuid || null;
    }

    private static _FormatAmount(amount: number): WaiterPayAmount {
        const _parts = Math.abs(amount).toString().split('.');
        const _int = _parts[0];
        const _dec = (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : '00';
        return { Int: _int, Dec: _dec, Str: `${_int},${_dec}`, Neg: (amount < 0) };
    }
}
